import { formatPhoneNumber } from '@/utils';
import { Flex, Input, Select } from '@contiple/antd-ds';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface PhoneInputProps {
  value: string;
  onChange?: (value: string) => void;
  countryCode: string;
  onCountryCodeChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  displayFormat?: boolean;
  inputWidth?: string | number;
}

// Define the country codes list
const COUNTRY_CODES = [
  // Main
  {
    code: '82',
    name: '대한민국',
    enName: 'Korea',
    nativeName: '한국',
    display: '(+82) 대한민국',
  },

  // Specified
  {
    code: '81',
    name: '일본',
    enName: 'Japan',
    nativeName: '日本',
    display: '(+81) 日本',
  },
  {
    code: '1',
    name: '미국',
    enName: 'United States',
    nativeName: 'United States',
    display: '(+1) United States',
  },
  {
    code: '86',
    name: '중국',
    enName: 'China',
    nativeName: '中国',
    display: '(+86) 中国',
  },
  {
    code: '60',
    name: '말레이시아',
    enName: 'Malaysia',
    nativeName: 'Malaysia',
    display: '(+60) Malaysia',
  },
  {
    code: '62',
    name: '인도네시아',
    enName: 'Indonesia',
    nativeName: 'Indonesia',
    display: '(+62) Indonesia',
  },
  {
    code: '65',
    name: '싱가포르',
    enName: 'Singapore',
    nativeName: 'Singapore',
    display: '(+65) Singapore',
  },
  {
    code: '66',
    name: '태국',
    enName: 'Thailand',
    nativeName: 'ไทย',
    display: '(+66) ไทย',
  },
  {
    code: '33',
    name: '프랑스',
    enName: 'France',
    nativeName: 'France',
    display: '(+33) France',
  },
  {
    code: '39',
    name: '이탈리아',
    enName: 'Italy',
    nativeName: 'Italia',
    display: '(+39) Italia',
  },
  {
    code: '44',
    name: '영국',
    enName: 'United Kingdom',
    nativeName: 'United Kingdom',
    display: '(+44) United Kingdom',
  },
  {
    code: '49',
    name: '독일',
    enName: 'Germany',
    nativeName: 'Deutschland',
    display: '(+49) Deutschland',
  },
  {
    code: '61',
    name: '호주',
    enName: 'Australia',
    nativeName: 'Australia',
    display: '(+61) Australia',
  },
  {
    code: '55',
    name: '브라질',
    enName: 'Brazil',
    nativeName: 'Brasil',
    display: '(+55) Brasil',
  },
  {
    code: '34',
    name: '스페인',
    enName: 'Spain',
    nativeName: 'España',
    display: '(+34) España',
  },

  // Remaining
  {
    code: '7',
    name: '러시아',
    enName: 'Russia',
    nativeName: 'Россия',
    display: '(+7) Россия',
  },
  {
    code: '91',
    name: '인도',
    enName: 'India',
    nativeName: 'भारत',
    display: '(+91) भारत',
  },
  {
    code: '92',
    name: '파키스탄',
    enName: 'Pakistan',
    nativeName: 'پاکستان',
    display: '(+92) پاکستان',
  },
  {
    code: '880',
    name: '방글라데시',
    enName: 'Bangladesh',
    nativeName: 'বাংলাদেশ',
    display: '(+880) বাংলাদেশ',
  },
  {
    code: '52',
    name: '멕시코',
    enName: 'Mexico',
    nativeName: 'México',
    display: '(+52) México',
  },
  {
    code: '852',
    name: '홍콩',
    enName: 'Hong Kong',
    nativeName: '香港',
    display: '(+852) 香港',
  },
  {
    code: '853',
    name: '마카오',
    enName: 'Macao',
    nativeName: '澳門',
    display: '(+853) 澳門',
  },
  {
    code: '850',
    name: '조선민주주의인민공화국',
    enName: 'North Korea',
    nativeName: '북한',
    display: '(+850) 북한',
  },
  {
    code: '95',
    name: '미얀마',
    enName: 'Myanmar',
    nativeName: 'မြန်မာ',
    display: '(+95) မြန်မာ',
  },
  {
    code: '856',
    name: '라오스',
    enName: 'Laos',
    nativeName: 'ລາວ',
    display: '(+856) ລາວ',
  },
  {
    code: '855',
    name: '캄보디아',
    enName: 'Cambodia',
    nativeName: 'កម្ពុជា',
    display: '(+855) កម្ពុជា',
  },
  {
    code: '670',
    name: '동티모르',
    enName: 'East Timor',
    nativeName: 'Timor-Leste',
    display: '(+670) Timor-Leste',
  },
  {
    code: '673',
    name: '브루나이',
    enName: 'Brunei',
    nativeName: 'Brunei',
    display: '(+673) Brunei',
  },
  {
    code: '674',
    name: '나우루',
    enName: 'Nauru',
    nativeName: 'Nauru',
    display: '(+674) Nauru',
  },
  {
    code: '675',
    name: '파푸아뉴기니',
    enName: 'Papua New Guinea',
    nativeName: 'Papua New Guinea',
    display: '(+675) Papua New Guinea',
  },
  {
    code: '676',
    name: '통가',
    enName: 'Tonga',
    nativeName: 'Tonga',
    display: '(+676) Tonga',
  },
  {
    code: '677',
    name: '솔로몬 제도',
    enName: 'Solomon Islands',
    nativeName: 'Solomon Islands',
    display: '(+677) Solomon Islands',
  },
  {
    code: '678',
    name: '바누아투',
    enName: 'Vanuatu',
    nativeName: 'Vanuatu',
    display: '(+678) Vanuatu',
  },
  {
    code: '679',
    name: '피지',
    enName: 'Fiji',
    nativeName: 'Fiji',
    display: '(+679) Fiji',
  },
  {
    code: '680',
    name: '팔라우',
    enName: 'Palau',
    nativeName: 'Palau',
    display: '(+680) Palau',
  },
  {
    code: '681',
    name: '월리스 푸투나',
    enName: 'Wallis and Futuna',
    nativeName: 'Wallis-et-Futuna',
    display: '(+681) Wallis-et-Futuna',
  },
  {
    code: '682',
    name: '쿡 제도',
    enName: 'Cook Islands',
    nativeName: 'Cook Islands',
    display: '(+682) Cook Islands',
  },
  {
    code: '683',
    name: '니우에',
    enName: 'Niue',
    nativeName: 'Niue',
    display: '(+683) Niue',
  },
  {
    code: '685',
    name: '사모아',
    enName: 'Samoa',
    nativeName: 'Samoa',
    display: '(+685) Samoa',
  },
  {
    code: '686',
    name: '키리바시',
    enName: 'Kiribati',
    nativeName: 'Kiribati',
    display: '(+686) Kiribati',
  },
  {
    code: '687',
    name: '뉴칼레도니아',
    enName: 'New Caledonia',
    nativeName: 'Nouvelle-Calédonie',
    display: '(+687) Nouvelle-Calédonie',
  },
  {
    code: '688',
    name: '투발루',
    enName: 'Tuvalu',
    nativeName: 'Tuvalu',
    display: '(+688) Tuvalu',
  },
  {
    code: '689',
    name: '프랑스령 폴리네시아',
    enName: 'French Polynesia',
    nativeName: 'Polynésie française',
    display: '(+689) Polynésie française',
  },
  {
    code: '690',
    name: '토켈라우',
    enName: 'Tokelau',
    nativeName: 'Tokelau',
    display: '(+690) Tokelau',
  },
  {
    code: '691',
    name: '미크로네시아',
    enName: 'Micronesia',
    nativeName: 'Micronesia',
    display: '(+691) Micronesia',
  },
  {
    code: '692',
    name: '마셜 제도',
    enName: 'Marshall Islands',
    nativeName: 'Marshall Islands',
    display: '(+692) Marshall Islands',
  },
  {
    code: '886',
    name: '대만',
    enName: 'Taiwan',
    nativeName: '臺灣',
    display: '(+886) 臺灣',
  },
  {
    code: '960',
    name: '몰디브',
    enName: 'Maldives',
    nativeName: 'ދިވެހިރާއްޖޭ',
    display: '(+960) ދިވެހިރާއްޖޭ',
  },
  {
    code: '961',
    name: '레바논',
    enName: 'Lebanon',
    nativeName: 'لبنان',
    display: '(+961) لبنان',
  },
  {
    code: '962',
    name: '요르단',
    enName: 'Jordan',
    nativeName: 'الأردن',
    display: '(+962) الأردن',
  },
  {
    code: '963',
    name: '시리아',
    enName: 'Syria',
    nativeName: 'سوريا',
    display: '(+963) سوريا',
  },
  {
    code: '964',
    name: '이라크',
    enName: 'Iraq',
    nativeName: 'العراق',
    display: '(+964) العراق',
  },
  {
    code: '965',
    name: '쿠웨이트',
    enName: 'Kuwait',
    nativeName: 'الكويت',
    display: '(+965) الكويت',
  },
  {
    code: '966',
    name: '사우디아라비아',
    enName: 'Saudi Arabia',
    nativeName: 'المملكة العربية السعودية',
    display: '(+966) المملكة العربية السعودية',
  },
  {
    code: '967',
    name: '예멘',
    enName: 'Yemen',
    nativeName: 'اليمن',
    display: '(+967) اليمن',
  },
  {
    code: '968',
    name: '오만',
    enName: 'Oman',
    nativeName: 'عمان',
    display: '(+968) عمان',
  },
  {
    code: '971',
    name: '아랍에미리트',
    enName: 'UAE',
    nativeName: 'الإمارات العربية المتحدة',
    display: '(+971) الإمارات العربية المتحدة',
  },
  {
    code: '972',
    name: '이스라엘',
    enName: 'Israel',
    nativeName: 'ישראל',
    display: '(+972) ישראל',
  },
  {
    code: '973',
    name: '바레인',
    enName: 'Bahrain',
    nativeName: 'البحرين',
    display: '(+973) البحرين',
  },
  {
    code: '974',
    name: '카타르',
    enName: 'Qatar',
    nativeName: 'قطر',
    display: '(+974) قطر',
  },
  {
    code: '975',
    name: '부탄',
    enName: 'Bhutan',
    nativeName: 'འབྲུག་ཡུལ',
    display: '(+975) འབྲུག་ཡུལ',
  },
  {
    code: '976',
    name: '몽골',
    enName: 'Mongolia',
    nativeName: 'Монгол улс',
    display: '(+976) Монгол улс',
  },
  {
    code: '977',
    name: '네팔',
    enName: 'Nepal',
    nativeName: 'नेपाल',
    display: '(+977) नेपाल',
  },
  {
    code: '98',
    name: '이란',
    enName: 'Iran',
    nativeName: 'ایران',
    display: '(+98) ایران',
  },
  {
    code: '992',
    name: '타지키스탄',
    enName: 'Tajikistan',
    nativeName: 'Тоҷикистон',
    display: '(+992) Тоҷикистон',
  },
  {
    code: '993',
    name: '투르크메니스탄',
    enName: 'Turkmenistan',
    nativeName: 'Türkmenistan',
    display: '(+993) Türkmenistan',
  },
  {
    code: '994',
    name: '아제르바이잔',
    enName: 'Azerbaijan',
    nativeName: 'Azərbaycan',
    display: '(+994) Azərbaycan',
  },
  {
    code: '995',
    name: '조지아',
    enName: 'Georgia',
    nativeName: 'საქართველო',
    display: '(+995) საქართველო',
  },
  {
    code: '996',
    name: '키르기스스탄',
    enName: 'Kyrgyzstan',
    nativeName: 'Кыргызстан',
    display: '(+996) Кыргызстан',
  },
  {
    code: '998',
    name: '우즈베키스탄',
    enName: 'Uzbekistan',
    nativeName: 'Ozbekiston',
    display: '(+998) Ozbekiston',
  },
  {
    code: '20',
    name: '이집트',
    enName: 'Egypt',
    nativeName: 'مصر',
    display: '(+20) مصر',
  },
  {
    code: '211',
    name: '남수단',
    enName: 'South Sudan',
    nativeName: 'South Sudan',
    display: '(+211) South Sudan',
  },
  {
    code: '212',
    name: '모로코',
    enName: 'Morocco',
    nativeName: 'المغرب',
    display: '(+212) المغرب',
  },
  {
    code: '213',
    name: '알제리',
    enName: 'Algeria',
    nativeName: 'الجزائر',
    display: '(+213)الجزائر',
  },
  {
    code: '216',
    name: '튀니지',
    enName: 'Tunisia',
    nativeName: 'تونس',
    display: '(+216) تونس',
  },
  {
    code: '218',
    name: '리비아',
    enName: 'Libya',
    nativeName: 'ليبيا',
    display: '(+218) ليبيا',
  },
  {
    code: '220',
    name: '감비아',
    enName: 'Gambia',
    nativeName: 'Gambia',
    display: '(+220) Gambia',
  },
  {
    code: '221',
    name: '세네갈',
    enName: 'Senegal',
    nativeName: 'Sénégal',
    display: '(+221) Sénégal',
  },
  {
    code: '222',
    name: '모리타니',
    enName: 'Mauritania',
    nativeName: 'موريتانيا',
    display: '(+222) موريتانيا',
  },
  {
    code: '223',
    name: '말리',
    enName: 'Mali',
    nativeName: 'Mali',
    display: '(+223) Mali',
  },
  {
    code: '224',
    name: '기니',
    enName: 'Guinea',
    nativeName: 'Guinée',
    display: '(+224) Guinée',
  },
  {
    code: '225',
    name: '코트디부아르',
    enName: "Côte d'Ivoire",
    nativeName: "Côte d'Ivoire",
    display: "(+225) Côte d'Ivoire",
  },
  {
    code: '226',
    name: '부르키나파소',
    enName: 'Burkina Faso',
    nativeName: 'Burkina Faso',
    display: '(+226) Burkina Faso',
  },
  {
    code: '227',
    name: '니제르',
    enName: 'Niger',
    nativeName: 'Niger',
    display: '(+227) Niger',
  },
  {
    code: '228',
    name: '토고',
    enName: 'Togo',
    nativeName: 'Togo',
    display: '(+228) Togo',
  },
  {
    code: '229',
    name: '베냉',
    enName: 'Benin',
    nativeName: 'Bénin',
    display: '(+229) Bénin',
  },
  {
    code: '230',
    name: '모리셔스',
    enName: 'Mauritius',
    nativeName: 'Maurice',
    display: '(+230) Maurice',
  },
  {
    code: '231',
    name: '라이베리아',
    enName: 'Liberia',
    nativeName: 'Liberia',
    display: '(+231) Liberia',
  },
  {
    code: '232',
    name: '시에라리온',
    enName: 'Sierra Leone',
    nativeName: 'Sierra Leone',
    display: '(+232) Sierra Leone',
  },
  {
    code: '233',
    name: '가나',
    enName: 'Ghana',
    nativeName: 'Ghana',
    display: '(+233) Ghana',
  },
  {
    code: '234',
    name: '나이지리아',
    enName: 'Nigeria',
    nativeName: 'Nigeria',
    display: '(+234) Nigeria',
  },
  {
    code: '235',
    name: '차드',
    enName: 'Chad',
    nativeName: 'Tchad',
    display: '(+235) Tchad',
  },
  {
    code: '236',
    name: '중앙아프리카 공화국',
    enName: 'Central African Republic',
    nativeName: 'République centrafricaine',
    display: '(+236) République centrafricaine',
  },
  {
    code: '237',
    name: '카메룬',
    enName: 'Cameroon',
    nativeName: 'Cameroun',
    display: '(+237) Cameroun',
  },
  {
    code: '238',
    name: '카보베르데',
    enName: 'Cape Verde',
    nativeName: 'Cabo Verde',
    display: '(+238) Cabo Verde',
  },
  {
    code: '239',
    name: '상투메프린시페',
    enName: 'São Tomé and Príncipe',
    nativeName: 'São Tomé e Príncipe',
    display: '(+239) São Tomé e Príncipe',
  },
  {
    code: '240',
    name: '적도 기니',
    enName: 'Equatorial Guinea',
    nativeName: 'Guinea Ecuatorial',
    display: '(+240) Guinea Ecuatorial',
  },
  {
    code: '241',
    name: '가봉',
    enName: 'Gabon',
    nativeName: 'Gabon',
    display: '(+241) Gabon',
  },
  {
    code: '242',
    name: '콩고 공화국',
    enName: 'Republic of the Congo',
    nativeName: 'République du Congo',
    display: '(+242) République du Congo',
  },
  {
    code: '243',
    name: '콩고 민주 공화국',
    enName: 'Democratic Republic of the Congo',
    nativeName: 'République démocratique du Congo',
    display: '(+243) République démocratique du Congo',
  },
  {
    code: '244',
    name: '앙골라',
    enName: 'Angola',
    nativeName: 'Angola',
    display: '(+244) Angola',
  },
  {
    code: '245',
    name: '기니비사우',
    enName: 'Guinea-Bissau',
    nativeName: 'Guiné-Bissau',
    display: '(+245) Guiné-Bissau',
  },
  {
    code: '247',
    name: '어센션 섬',
    enName: 'Ascension Island',
    nativeName: 'Ascension Island',
    display: '(+247) Ascension Island',
  },
  {
    code: '248',
    name: '세이셸',
    enName: 'Seychelles',
    nativeName: 'Seychelles',
    display: '(+248) Seychelles',
  },
  {
    code: '249',
    name: '수단',
    enName: 'Sudan',
    nativeName: 'السودان',
    display: '(+249) السودان',
  },
  {
    code: '250',
    name: '르완다',
    enName: 'Rwanda',
    nativeName: 'Rwanda',
    display: '(+250) Rwanda',
  },
  {
    code: '251',
    name: '에티오피아',
    enName: 'Ethiopia',
    nativeName: 'ኢትዮጵያ',
    display: '(+251) ኢትዮጵያ',
  },
  {
    code: '252',
    name: '소말리아',
    enName: 'Somalia',
    nativeName: 'Somalia',
    display: '(+252) Somalia',
  },
  {
    code: '253',
    name: '지부티',
    enName: 'Djibouti',
    nativeName: 'Djibouti',
    display: '(+253) Djibouti',
  },
  {
    code: '254',
    name: '케냐',
    enName: 'Kenya',
    nativeName: 'Kenya',
    display: '(+254) Kenya',
  },
  {
    code: '255',
    name: '탄자니아',
    enName: 'Tanzania',
    nativeName: 'Tanzania',
    display: '(+255) Tanzania',
  },
  {
    code: '256',
    name: '우간다',
    enName: 'Uganda',
    nativeName: 'Uganda',
    display: '(+256) Uganda',
  },
  {
    code: '257',
    name: '부룬디',
    enName: 'Burundi',
    nativeName: 'Burundi',
    display: '(+257) Burundi',
  },
  {
    code: '258',
    name: '모잠비크',
    enName: 'Mozambique',
    nativeName: 'Moçambique',
    display: '(+258) Moçambique',
  },
  {
    code: '260',
    name: '잠비아',
    enName: 'Zambia',
    nativeName: 'Zambia',
    display: '(+260) Zambia',
  },
  {
    code: '261',
    name: '마다가스카르',
    enName: 'Madagascar',
    nativeName: 'Madagascar',
    display: '(+261) Madagascar',
  },
  {
    code: '262',
    name: '레위니옹',
    enName: 'Réunion',
    nativeName: 'La Réunion',
    display: '(+262) La Réunion',
  },
  {
    code: '263',
    name: '짐바브웨',
    enName: 'Zimbabwe',
    nativeName: 'Zimbabwe',
    display: '(+263) Zimbabwe',
  },
  {
    code: '264',
    name: '나미비아',
    enName: 'Namibia',
    nativeName: 'Namibia',
    display: '(+264) Namibia',
  },
  {
    code: '265',
    name: '말라위',
    enName: 'Malawi',
    nativeName: 'Malawi',
    display: '(+265) Malawi',
  },
  {
    code: '266',
    name: '레소토',
    enName: 'Lesotho',
    nativeName: 'Lesotho',
    display: '(+266) Lesotho',
  },
  {
    code: '267',
    name: '보츠와나',
    enName: 'Botswana',
    nativeName: 'Botswana',
    display: '(+267) Botswana',
  },
  {
    code: '268',
    name: '에스와티니',
    enName: 'Eswatini',
    nativeName: 'eSwatini',
    display: '(+268) eSwatini',
  },
  {
    code: '269',
    name: '코모로',
    enName: 'Comoros',
    nativeName: 'Comores',
    display: '(+269) Comores',
  },
  {
    code: '290',
    name: '세인트헬레나',
    enName: 'Saint Helena',
    nativeName: 'Saint Helena',
    display: '(+290) Saint Helena',
  },
  {
    code: '291',
    name: '에리트레아',
    enName: 'Eritrea',
    nativeName: 'ኤርትራ',
    display: '(+291) ኤርትራ',
  },
  {
    code: '297',
    name: '아루바',
    enName: 'Aruba',
    nativeName: 'Aruba',
    display: '(+297) Aruba',
  },
  {
    code: '298',
    name: '페로 제도',
    enName: 'Faroe Islands',
    nativeName: 'Føroyar',
    display: '(+298) Føroyar',
  },
  {
    code: '299',
    name: '그린란드',
    enName: 'Greenland',
    nativeName: 'Kalaallit Nunaat',
    display: '(+299) Kalaallit Nunaat',
  },
  {
    code: '350',
    name: '지브롤터',
    enName: 'Gibraltar',
    nativeName: 'Gibraltar',
    display: '(+350) Gibraltar',
  },
  {
    code: '351',
    name: '포르투갈',
    enName: 'Portugal',
    nativeName: 'Portugal',
    display: '(+351) Portugal',
  },
  {
    code: '352',
    name: '룩셈부르크',
    enName: 'Luxembourg',
    nativeName: 'Luxembourg',
    display: '(+352) Luxembourg',
  },
  {
    code: '353',
    name: '아일랜드',
    enName: 'Ireland',
    nativeName: 'Éire',
    display: '(+353) Éire',
  },
  {
    code: '354',
    name: '아이슬란드',
    enName: 'Iceland',
    nativeName: 'Ísland',
    display: '(+354) Ísland',
  },
  {
    code: '355',
    name: '알바니아',
    enName: 'Albania',
    nativeName: 'Shqipëria',
    display: '(+355) Shqipëria',
  },
  {
    code: '356',
    name: '몰타',
    enName: 'Malta',
    nativeName: 'Malta',
    display: '(+356) Malta',
  },
  {
    code: '357',
    name: '키프로스',
    enName: 'Cyprus',
    nativeName: 'Κύπρος',
    display: '(+357) Κύπρος',
  },
  {
    code: '358',
    name: '핀란드',
    enName: 'Finland',
    nativeName: 'Suomi',
    display: '(+358) Suomi',
  },
  {
    code: '359',
    name: '불가리아',
    enName: 'Bulgaria',
    nativeName: 'България',
    display: '(+359) България',
  },
  {
    code: '370',
    name: '리투아니아',
    enName: 'Lithuania',
    nativeName: 'Lietuva',
    display: '(+370) Lietuva',
  },
  {
    code: '371',
    name: '라트비아',
    enName: 'Latvia',
    nativeName: 'Latvija',
    display: '(+371) Latvija',
  },
  {
    code: '372',
    name: '에스토니아',
    enName: 'Estonia',
    nativeName: 'Eesti',
    display: '(+372) Eesti',
  },
  {
    code: '373',
    name: '몰도바',
    enName: 'Moldova',
    nativeName: 'Moldova',
    display: '(+373) Moldova',
  },
  {
    code: '374',
    name: '아르메니아',
    enName: 'Armenia',
    nativeName: 'Հայաստան',
    display: '(+374) Հայաստան',
  },
  {
    code: '375',
    name: '벨라루스',
    enName: 'Belarus',
    nativeName: 'Беларусь',
    display: '(+375) Беларусь',
  },
  {
    code: '376',
    name: '안도라',
    enName: 'Andorra',
    nativeName: 'Andorra',
    display: '(+376) Andorra',
  },
  {
    code: '377',
    name: '모나코',
    enName: 'Monaco',
    nativeName: 'Monaco',
    display: '(+377) Monaco',
  },
  {
    code: '378',
    name: '산마리노',
    enName: 'San Marino',
    nativeName: 'San Marino',
    display: '(+378) San Marino',
  },
  {
    code: '380',
    name: '우크라이나',
    enName: 'Ukraine',
    nativeName: 'Україна',
    display: '(+380) Україна',
  },
  {
    code: '381',
    name: '세르비아',
    enName: 'Serbia',
    nativeName: 'Србија',
    display: '(+381) Србија',
  },
  {
    code: '382',
    name: '몬테네그로',
    enName: 'Montenegro',
    nativeName: 'Црна Гора',
    display: '(+382) Црна Гора',
  },
  {
    code: '383',
    name: '코소보',
    enName: 'Kosovo',
    nativeName: 'Kosovë',
    display: '(+383) Kosovë',
  },
  {
    code: '385',
    name: '크로아티아',
    enName: 'Croatia',
    nativeName: 'Hrvatska',
    display: '(+385) Hrvatska',
  },
  {
    code: '386',
    name: '슬로베니아',
    enName: 'Slovenia',
    nativeName: 'Slovenija',
    display: '(+386) Slovenija',
  },
  {
    code: '387',
    name: '보스니아 헤르체고비나',
    enName: 'Bosnia and Herzegovina',
    nativeName: 'Bosna i Hercegovina',
    display: '(+387) Bosna i Hercegovina',
  },
  {
    code: '389',
    name: '북마케도니아',
    enName: 'North Macedonia',
    nativeName: 'Северна Македонија',
    display: '(+389) Северна Македонија',
  },
  {
    code: '420',
    name: '체코',
    enName: 'Czech Republic',
    nativeName: 'Česká republika',
    display: '(+420) Česká republika',
  },
  {
    code: '421',
    name: '슬로바키아',
    enName: 'Slovakia',
    nativeName: 'Slovensko',
    display: '(+421) Slovensko',
  },
  {
    code: '423',
    name: '리히텐슈타인',
    enName: 'Liechtenstein',
    nativeName: 'Liechtenstein',
    display: '(+423) Liechtenstein',
  },
  {
    code: '500',
    name: '포클랜드 제도',
    enName: 'Falkland Islands',
    nativeName: 'Falkland Islands',
    display: '(+500) Falkland Islands',
  },
  {
    code: '501',
    name: '벨리즈',
    enName: 'Belize',
    nativeName: 'Belize',
    display: '(+501) Belize',
  },
  {
    code: '502',
    name: '과테말라',
    enName: 'Guatemala',
    nativeName: 'Guatemala',
    display: '(+502) Guatemala',
  },
  {
    code: '503',
    name: '엘살바도르',
    enName: 'El Salvador',
    nativeName: 'El Salvador',
    display: '(+503) El Salvador',
  },
  {
    code: '504',
    name: '온두라스',
    enName: 'Honduras',
    nativeName: 'Honduras',
    display: '(+504) Honduras',
  },
  {
    code: '505',
    name: '니카라과',
    enName: 'Nicaragua',
    nativeName: 'Nicaragua',
    display: '(+505) Nicaragua',
  },
  {
    code: '506',
    name: '코스타리카',
    enName: 'Costa Rica',
    nativeName: 'Costa Rica',
    display: '(+506) Costa Rica',
  },
  {
    code: '507',
    name: '파나마',
    enName: 'Panama',
    nativeName: 'Panamá',
    display: '(+507) Panamá',
  },
  {
    code: '508',
    name: '생피에르 미클롱',
    enName: 'Saint Pierre and Miquelon',
    nativeName: 'Saint-Pierre-et-Miquelon',
    display: '(+508) Saint-Pierre-et-Miquelon',
  },
  {
    code: '509',
    name: '아이티',
    enName: 'Haiti',
    nativeName: 'Haïti',
    display: '(+509) Haïti',
  },
  {
    code: '590',
    name: '과들루프',
    enName: 'Guadeloupe',
    nativeName: 'Guadeloupe',
    display: '(+590) Guadeloupe',
  },
  {
    code: '591',
    name: '볼리비아',
    enName: 'Bolivia',
    nativeName: 'Bolivia',
    display: '(+591) Bolivia',
  },
  {
    code: '592',
    name: '가이아나',
    enName: 'Guyana',
    nativeName: 'Guyana',
    display: '(+592) Guyana',
  },
  {
    code: '593',
    name: '에콰도르',
    enName: 'Ecuador',
    nativeName: 'Ecuador',
    display: '(+593) Ecuador',
  },
  {
    code: '594',
    name: '프랑스령 기아나',
    enName: 'French Guiana',
    nativeName: 'Guyane française',
    display: '(+594) Guyane française',
  },
  {
    code: '595',
    name: '파라과이',
    enName: 'Paraguay',
    nativeName: 'Paraguay',
    display: '(+595) Paraguay',
  },
  {
    code: '596',
    name: '마르티니크',
    enName: 'Martinique',
    nativeName: 'Martinique',
    display: '(+596) Martinique',
  },
  {
    code: '597',
    name: '수리남',
    enName: 'Suriname',
    nativeName: 'Suriname',
    display: '(+597) Suriname',
  },
  {
    code: '598',
    name: '우루과이',
    enName: 'Uruguay',
    nativeName: 'Uruguay',
    display: '(+598) Uruguay',
  },
  {
    code: '599',
    name: '퀴라소',
    enName: 'Curaçao',
    nativeName: 'Curaçao',
    display: '(+599) Curaçao',
  },
  {
    code: '1242',
    name: '바하마',
    enName: 'Bahamas',
    nativeName: 'Bahamas',
    display: '(+1242) Bahamas',
  },
  {
    code: '1246',
    name: '바베이도스',
    enName: 'Barbados',
    nativeName: 'Barbados',
    display: '(+1246) Barbados',
  },
  {
    code: '1264',
    name: '앵귈라',
    enName: 'Anguilla',
    nativeName: 'Anguilla',
    display: '(+1264) Anguilla',
  },
  {
    code: '1268',
    name: '앤티가 바부다',
    enName: 'Antigua and Barbuda',
    nativeName: 'Antigua and Barbuda',
    display: '(+1268) Antigua and Barbuda',
  },
  {
    code: '1284',
    name: '영국령 버진아일랜드',
    enName: 'British Virgin Islands',
    nativeName: 'British Virgin Islands',
    display: '(+1284) British Virgin Islands',
  },
  {
    code: '1340',
    name: '미국령 버진아일랜드',
    enName: 'US Virgin Islands',
    nativeName: 'US Virgin Islands',
    display: '(+1340) US Virgin Islands',
  },
  {
    code: '1345',
    name: '케이맨 제도',
    enName: 'Cayman Islands',
    nativeName: 'Cayman Islands',
    display: '(+1345) Cayman Islands',
  },
  {
    code: '1441',
    name: '버뮤다',
    enName: 'Bermuda',
    nativeName: 'Bermuda',
    display: '(+1441) Bermuda',
  },
  {
    code: '1473',
    name: '그레나다',
    enName: 'Grenada',
    nativeName: 'Grenada',
    display: '(+1473) Grenada',
  },
  {
    code: '1649',
    name: '터크스 케이커스 제도',
    enName: 'Turks and Caicos Islands',
    nativeName: 'Turks and Caicos Islands',
    display: '(+1649) Turks and Caicos Islands',
  },
  {
    code: '1664',
    name: '몬트세랫',
    enName: 'Montserrat',
    nativeName: 'Montserrat',
    display: '(+1664) Montserrat',
  },
  {
    code: '1670',
    name: '북마리아나 제도',
    enName: 'Northern Mariana Islands',
    nativeName: 'Northern Mariana Islands',
    display: '(+1670) Northern Mariana Islands',
  },
  {
    code: '1671',
    name: '괌',
    enName: 'Guam',
    nativeName: 'Guam',
    display: '(+1671) Guam',
  },
  {
    code: '1684',
    name: '아메리칸 사모아',
    enName: 'American Samoa',
    nativeName: 'American Samoa',
    display: '(+1684) American Samoa',
  },
  {
    code: '1758',
    name: '세인트루시아',
    enName: 'Saint Lucia',
    nativeName: 'Saint Lucia',
    display: '(+1758) Saint Lucia',
  },
  {
    code: '1767',
    name: '도미니카',
    enName: 'Dominica',
    nativeName: 'Dominica',
    display: '(+1767) Dominica',
  },
  {
    code: '1784',
    name: '세인트빈센트 그레나딘',
    enName: 'Saint Vincent and the Grenadines',
    nativeName: 'Saint Vincent and the Grenadines',
    display: '(+1784) Saint Vincent and the Grenadines',
  },
  {
    code: '1787',
    name: '푸에르토리코',
    enName: 'Puerto Rico',
    nativeName: 'Puerto Rico',
    display: '(+1787) Puerto Rico',
  },
  {
    code: '1809',
    name: '도미니카 공화국',
    enName: 'Dominican Republic',
    nativeName: 'República Dominicana',
    display: '(+1809) República Dominicana',
  },
  {
    code: '1868',
    name: '트리니다드 토바고',
    enName: 'Trinidad and Tobago',
    nativeName: 'Trinidad and Tobago',
    display: '(+1868) Trinidad and Tobago',
  },
  {
    code: '1869',
    name: '세인트키츠 네비스',
    enName: 'Saint Kitts and Nevis',
    nativeName: 'Saint Kitts and Nevis',
    display: '(+1869) Saint Kitts and Nevis',
  },
  {
    code: '1876',
    name: '자메이카',
    enName: 'Jamaica',
    nativeName: 'Jamaica',
    display: '(+1876) Jamaica',
  },
];

export const PhoneInput = memo(function PhoneInput({
  value,
  onChange = () => {},
  countryCode,
  onCountryCodeChange,
  onBlur,
  placeholder,
  disabled,
  readOnly = false,
  className,
  displayFormat = true,
}: PhoneInputProps) {
  const MAX_DIGITS = 15;
  const [displayText, setDisplayText] = useState(value);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setDisplayText(value);
  }, [value]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      const digits = newValue.replace(/\D/g, '');

      if (digits.length <= MAX_DIGITS) {
        setDisplayText(digits);

        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }

        debounceRef.current = setTimeout(() => {
          const formattedNumber = formatPhoneNumber(countryCode, digits);
          onChange(formattedNumber);
        }, 300);
      }
    },
    [onChange, countryCode]
  );

  const displayValue = useMemo(
    () =>
      displayFormat ? formatPhoneNumber(countryCode, displayText) : displayText,
    [displayFormat, countryCode, displayText]
  );

  return (
    <Flex className={className} style={{ flex: 1 }} gap={8}>
      <Select
        value={countryCode}
        className={readOnly ? 'readonly' : ''}
        onChange={(v) => onCountryCodeChange?.(String(v))}
        disabled={disabled || readOnly}
        style={{ width: 140, flexShrink: 0 }}
        options={COUNTRY_CODES.map((code) => ({
          value: code.code,
          label: code.display,
        }))}
      />
      <Input
        value={displayValue}
        onChange={handleInputChange}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        type="tel"
        style={{ flex: 1 }}
      />
    </Flex>
  );
});

export default PhoneInput;
